import Chart from 'react-apexcharts';
import React, { FC } from 'react'
import { ID } from '@/_metronic/helpers'
import { useState, useEffect } from 'react'
import { TrxStatusData } from '../core/_models'
import { getTrxStatus } from '../core/_requests'
import { getCSSVariableValue } from '@/_metronic/assets/ts/_utils'
import { useIntl } from 'react-intl'

type Props = {
  userId?: ID
  from: Date
  paymode?: string
  to: Date,
  height: number
  timezone: string;
}
const TrxStatusChart: FC<Props> = ({ userId, from, to, height, paymode, timezone }) => {
  const intl = useIntl()
  const [data, setData] = useState<Array<TrxStatusData> | undefined>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const res = await getTrxStatus({ from, to, userId, paymode, timezone })
        setData(res || [])

      } catch (e: unknown) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [userId, from, to, paymode])

  const getColorClass = (status: string) => {
    switch (status) {
      case 'FAILED':
        return 'danger'
      case 'SUCCEED':
        return 'success'
      case 'PENDING':
        return 'primary'
      default:
        return 'warning'
    }
  }

  const series = (data || []).map((d) => d.trx)
  const max = (data || []).reduce((acc, curr) => acc + curr.trx, 0)
  const options = {
    chart: {
      id: 'trx-status',
      toolbar: { show: false },
      zoom: { enabled: false },
      sparkline: {
        enabled: true,
      },
    },
    labels: (data || []).map((d) =>
      intl.formatMessage({ id: `P2C.TRX_STATUS_${d.status}` })
    ),
    legend: {
      show: false
    },
    colors: (data || []).map((d: TrxStatusData) =>
      getCSSVariableValue(`--bs-text-${getColorClass(d.status)}`)
    )
  }

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <div className="card-title">
          <span className="card-label fw-bold fs-3 mb-1">
            Transactions status
            {paymode && (
              <i className="text-danger ms-1">
                {intl.formatMessage({ id: `P2C.PAYMODE_${paymode.toUpperCase()}` })}
              </i>
            )}
          </span>
        </div>
      </div>

      <div className="card-body p-9">
        {isLoading ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <i className="spinner-border fs-5x" />
          </div>
        ) : (data === undefined || data.length === 0) ? (
          <div className="d-grid text-center" style={{ minHeight: `${height}px` }}>
            <span>No data</span>
          </div>
        ) : (
          <div className="row align-items-center mx-0 w-100">
            <div className="col-6 d-flex justify-content-end px-0">
              <Chart options={options} series={series} type="donut" height={height} />
            </div>
            <div className="col-6 px-0">
              <div className="d-flex flex-column justify-content-center">
                {data.map((item, i) => {
                  const color = getColorClass(item.status)
                  return (
                    <div key={i} className="d-flex fs-6 fw-semibold align-items-center">
                      <div className={`bullet bg-${color} me-3`} />
                      <div className="fs-5 fw-bold text-gray-600 me-5">{item.status}</div>
                      <div className="ms-auto fw-bolder text-gray-700 text-end">
                        {(Number(item.trx) * 100 / max).toFixed(2)}%
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TrxStatusChart
